var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { Select } from 'antd';
import { DesktopOutlined } from '@ant-design/icons';
import EmptyComponent from 'components/EmptyComponent';
import DataTable from 'library/components/DataTable';
import { codeColumn, mutedColumn, NameText, RowActions, RowButton, statusColumn } from 'library/components/DataTable/cells';
import ListScreen, { ListAddButton, listPager, usePagedRows } from 'library/components/ListScreen';
import localeTranslator from 'library/Hocs/LocalTranslator';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { ReceptionDesk as ReceptionDeskResource } from 'constants/resources';
import { ReceptionDesk } from 'models';
import AssignReceptionDeskEmployeesDrawer from 'pages/Settings/drawers/AssignReceptionDeskEmployeesDrawer';
import ReceptionDeskDrawer from 'pages/Settings/drawers/ReceptionDeskDrawer';
import services from 'services';
var ReceptionDesks = function (props) {
    var t = props.translate;
    var receptionDeskService = services.receptionDeskService, clinicService = services.clinicService, employeeService = services.employeeService, permissionService = services.permissionService;
    var _a = useState(''), searchValue = _a[0], setSearchValue = _a[1];
    var _b = useState('all'), clinicFilter = _b[0], setClinicFilter = _b[1];
    var _c = useState(false), deskDrawerVisible = _c[0], setDeskDrawerVisible = _c[1];
    var _d = useState(false), staffDrawerVisible = _d[0], setStaffDrawerVisible = _d[1];
    var _e = useState(null), currentDesk = _e[0], setCurrentDesk = _e[1];
    var _f = useState(false), isEdit = _f[0], setIsEdit = _f[1];
    var debounceRef = useRef(undefined);
    var clinics = clinicService.useClinics().data;
    var employees = employeeService.useEmployees().data;
    var canAdd = permissionService.hasPermissionToAdd(ReceptionDeskResource);
    var canEdit = permissionService.hasPermissionToEdit(ReceptionDeskResource);
    var canAssignStaff = permissionService.hasPermissionToAssignReceptionDeskEmployees();
    var showActions = canEdit || canAssignStaff;
    var _g = receptionDeskService.useReceptionDesks(), receptionDesks = _g.data, isLoading = _g.isLoading;
    var desks = receptionDesks || [];
    var clinicName = useCallback(function (desk) {
        var _a, _b;
        return ((_a = desk.getClinic()) === null || _a === void 0 ? void 0 : _a.getName()) ||
            ((_b = clinics === null || clinics === void 0 ? void 0 : clinics.find(function (clinic) { return clinic.getId() === desk.getClinicId(); })) === null || _b === void 0 ? void 0 : _b.getName()) ||
            '';
    }, [clinics]);
    var filtered = useMemo(function () {
        var query = searchValue.trim().toLowerCase();
        return desks.filter(function (desk) {
            if (clinicFilter !== 'all' && desk.getClinicId() !== clinicFilter)
                return false;
            if (!query)
                return true;
            var status = t(desk.isActive() ? 'receptionDesks.status.active' : 'receptionDesks.status.inactive');
            var haystack = [desk.getName(), desk.getCode(), clinicName(desk), status]
                .join(' ')
                .toLowerCase();
            return haystack.includes(query);
        });
    }, [desks, clinicFilter, searchValue, t, clinicName]);
    var _h = usePagedRows(filtered), page = _h.page, setPage = _h.setPage, pages = _h.pages, from = _h.from, to = _h.to, pageRows = _h.pageRows;
    var clinicOptions = useMemo(function () { return __spreadArray([
        { value: 'all', label: t('receptionDesks.allClinics') }
    ], (clinics || []).map(function (clinic) { return ({
        value: clinic.getId(),
        label: clinic.getName()
    }); }), true); }, [clinics, t]);
    var onSearchValueChange = function (event) {
        var value = event.target.value;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () {
            setPage(1);
            setSearchValue(value);
        }, 300);
    };
    var openCreate = function () {
        var desk = new ReceptionDesk();
        desk.setActive(true);
        setCurrentDesk(desk);
        setIsEdit(false);
        setDeskDrawerVisible(true);
    };
    var openEdit = useCallback(function (desk) {
        setCurrentDesk(plainToInstance(ReceptionDesk, instanceToPlain(desk)));
        setIsEdit(true);
        setDeskDrawerVisible(true);
    }, []);
    var openStaff = useCallback(function (desk) {
        setCurrentDesk(plainToInstance(ReceptionDesk, instanceToPlain(desk)));
        setStaffDrawerVisible(true);
    }, []);
    var onCloseDeskDrawer = function () {
        setDeskDrawerVisible(false);
        setCurrentDesk(null);
    };
    var onCloseStaffDrawer = function () {
        setStaffDrawerVisible(false);
        setCurrentDesk(null);
    };
    var columns = useMemo(function () { return __spreadArray([
        {
            key: 'name',
            title: t('receptionDesks.table.name'),
            width: 'minmax(200px, 1.6fr)',
            render: function (desk) {
                var _a;
                var active = desk.isActive();
                var staffCount = ((_a = desk.getEmployees()) === null || _a === void 0 ? void 0 : _a.length) || 0;
                return (_jsxs("div", { className: "cidx-who", children: [_jsx("span", { className: "cidx-tile ".concat(active ? '' : 'is-muted'), children: _jsx(DesktopOutlined, {}) }), _jsxs("div", { className: "cidx-stack", children: [_jsx(NameText, { children: desk.getName() }), staffCount > 0 ? (_jsxs("span", { className: "cidx-stack__sub", children: [t('receptionDesks.form.employees'), ": ", staffCount] })) : null] })] }));
            }
        },
        codeColumn({ key: 'code', title: t('receptionDesks.table.code'), width: '120px', value: function (desk) { return desk.getCode(); } }),
        mutedColumn({ key: 'clinic', title: t('receptionDesks.table.clinic'), width: 'minmax(160px, 1.2fr)', value: function (desk) { return clinicName(desk); } }),
        statusColumn({
            title: t('receptionDesks.table.status'),
            active: function (desk) { return desk.isActive(); },
            activeLabel: t('receptionDesks.status.active'),
            inactiveLabel: t('receptionDesks.status.inactive')
        })
    ], (showActions ? [{
            key: 'actions',
            title: t('receptionDesks.table.actions'),
            width: '180px',
            align: 'end',
            render: function (desk) { return (_jsxs(RowActions, { children: [canEdit ? _jsx(RowButton, { onClick: function () { return openEdit(desk); }, children: t('receptionDesks.actions.edit') }) : null, canAssignStaff ? (_jsx(RowButton, { accent: desk.isActive(), onClick: function () { return openStaff(desk); }, children: t('receptionDesks.assignStaff') })) : null] })); }
        }] : []), true); }, [t, clinicName, showActions, canEdit, canAssignStaff, openEdit, openStaff]);
    return (_jsxs(ListScreen, { title: t('receptionDesks.title'), count: t('receptionDesks.countBadge', { count: desks.length }), countTone: "slate", subtitle: t('receptionDesks.subtitle'), actions: canAdd ? _jsx(ListAddButton, { label: t('receptionDesks.addDesk'), onClick: openCreate }) : undefined, search: { placeholder: t('receptionDesks.searchPlaceholder'), onChange: onSearchValueChange }, toolbarEnd: (clinics || []).length > 1 ? (_jsx(Select, { className: "cidx-select", value: clinicFilter, onChange: function (value) {
                setClinicFilter(value);
                setPage(1);
            }, options: clinicOptions, popupMatchSelectWidth: false })) : (clinics === null || clinics === void 0 ? void 0 : clinics[0]) ? (_jsxs("div", { className: "cidx-facility", children: [_jsxs("span", { className: "cidx-facility__label", children: [t('receptionDesks.facility'), ":"] }), _jsx("span", { children: clinics[0].getName() })] })) : null, children: [_jsx(DataTable, { minWidth: showActions ? '820px' : '640px', loading: isLoading, rows: pageRows, rowKey: function (desk) { return desk.getId(); }, empty: _jsx(EmptyComponent, { description: t('receptionDesks.empty') }), columns: columns, pager: listPager({
                    total: filtered.length,
                    page: page,
                    pages: pages,
                    meta: t('receptionDesks.showingRange', { from: from, to: to, total: filtered.length }),
                    previous: t('receptionDesks.previous'),
                    next: t('receptionDesks.next'),
                    center: t('receptionDesks.pageOf', { page: page, pages: pages }),
                    setPage: setPage
                }) }), _jsx(ReceptionDeskDrawer, { visiable: deskDrawerVisible, onCloseDrawer: onCloseDeskDrawer, receptionDesk: currentDesk, clinics: clinics || [], isEdit: isEdit }), _jsx(AssignReceptionDeskEmployeesDrawer, { visiable: staffDrawerVisible, onCloseDrawer: onCloseStaffDrawer, receptionDesk: currentDesk, employees: employees || [] })] }));
};
export default localeTranslator(ReceptionDesks, 'settings');
